import { message } from 'antd';
import { AxiosError } from 'axios';
import { FormState } from 'final-form';
import arrayMutators from 'final-form-arrays';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { Form as FormRFF } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PUBLICATION_ERROR, PUBLICATION_MODE } from 'api/documents/documents.model';
import Card from 'components/Card';
import Title from 'components/Title';
import { useAddPublicDocumentMutation } from 'hooks/useAddPublicDocumentMutation';
import { useAuthenticatedContext } from 'hooks/useAuthenticated';
import useValidationSchema from 'hooks/useValidationSchema';
import { CertificateDetailsModel } from 'models/certificate.model';
import { DocumentInfoModel, CATEGORY_TYPE } from 'models/documents.model';

import NewCertForm from './NewCertForm';
import { generateBasicPDF } from './helpers/generateBasicPDF';
import { getInitialValues } from './helpers/getInitialValues';
import { overallRecycledPercentagesDecorator } from './helpers/overallRecycledPercentagesDecorator';
import { parseAdditionalDetails } from './helpers/parseAdditionalDetails';
import { getValidationSchema } from './helpers/validation';
import { CertificateTypeConfig } from './hooks/useCategoryConfig';

interface NewCertContentInterface {
  categoryId: number;
  type: CATEGORY_TYPE;
  config: CertificateTypeConfig;
}

const NewCertContent = ({ categoryId, type, config }: NewCertContentInterface) => {
  const { currentUser } = useAuthenticatedContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [submitError, setSubmitError] = useState<Record<string, string> | null>(null);

  const schema = getValidationSchema(type, config, t);

  const { mutate: addCertificate, isLoading } = useAddPublicDocumentMutation({
    onSuccess: () => {
      message.success(t('success.newCert'));
      navigate(`/home/certificates`);
    },
    onError: (error: AxiosError) => {
      if (error.response?.data.statusCode === PUBLICATION_ERROR.PRODUCT_ID_ALREADY_EXISTS) {
        setSubmitError({ productId: t('error.newCertInvalidProductId') });
      }

      message.error(t('error.newCert'));
    },
  });

  const publish = useCallback(
    async (formState: FormState<CertificateDetailsModel>, isDraft: boolean) => {
      const { values, hasValidationErrors } = formState;

      if (!hasValidationErrors) {
        const { additionalCertificate, title, ...certificateForm } = values;

        const currentDate = moment().utc();
        const validSince = currentDate.format('YYYY-MM-DD[T]HH:mm:ss');
        const validUntil = currentDate.add(10, 'y').format('YYYY-MM-DD[T]HH:mm:ss');

        const documentInfo: DocumentInfoModel = {
          title: title || certificateForm.productName,
          categoryId,
          draft: isDraft,
          publicationMode: PUBLICATION_MODE.NEW,
          validSince,
          validUntil,
          retainUntil: validUntil,
          additionalDetails: parseAdditionalDetails(type, {
            ...certificateForm,
            company: currentUser?.name || '',
          }),
        };

        const documentContent = additionalCertificate ?? generateBasicPDF();

        addCertificate({ documentInfo, documentContent });
      }
    },
    [categoryId, type, currentUser?.name, addCertificate]
  );

  const clearSubmitError = (data: Record<string, any>) => {
    setSubmitError(data);
  };

  const initialValues = getInitialValues(type);

  return (
    <Card backButtonLink="/home/new-certificate" padding="large">
      <Title>{`${t('certificate.newCertificate')}`}</Title>
      {t(`certificate.${config?.isDefault ? 'other' : type}`)}
      <FormRFF<CertificateDetailsModel>
        onSubmit={() => {}}
        validate={useValidationSchema(schema)}
        initialValues={initialValues}
        decorators={[overallRecycledPercentagesDecorator]}
        mutators={{
          ...arrayMutators,
          linkComponent: ([component, index], state, utils) => {
            utils.changeValue(state, `components[${index}]`, () => component);
          },
          setFile: ([file], state, utils) => {
            utils.changeValue(state, 'additionalCertificate', () => file);
          },
        }}
      >
        {({ handleSubmit, form, values }) => (
          <NewCertForm
            config={config}
            handleSubmit={handleSubmit}
            form={form}
            values={values}
            initialValues={initialValues}
            publish={publish}
            submitError={submitError}
            clearSubmitError={clearSubmitError}
            isLoading={isLoading}
          />
        )}
      </FormRFF>
    </Card>
  );
};

export default NewCertContent;
